import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { i18n, mediaQuery, showMessage, useGeneralSoContext } from 'src/common';
import { useFinishPayment } from 'src/common/api/apollo/mutations';
import { useGetOfferById } from 'src/common/api/apollo/queries';
import { ContainerComponent, Flex, ThankYouCard } from 'src/components';
import { footer, header } from 'src/theme';

const FlexStyle = styled(Flex)({
  minHeight: `calc(100vh - ${footer.mobileHeight + header.mobileHeight + 80}px)`,

  [mediaQuery('mobile')]: {
    minHeight: `calc(100vh - ${footer.height + header.mobileHeight + 80}px)`,
  },

  [mediaQuery('laptopL')]: {
    minHeight: `calc(100vh - ${footer.height + header.height + 80}px)`,
  },
});

type UrlParams = Record<string, string>;

const keyMapping: Record<string, string> = {
  CustomerFirstname: 'wsPayCustomerFirstName',
  CustomerSurname: 'wsPayCustomerLastName',
  ShoppingCartID: 'wsPayShoppingCartId',
  DateTime: 'wsPayDateTime',
  Amount: 'wsPayAmount',
  STAN: 'wsPaySTAN',
  Partner: 'wsPayPartner',
  WsPayOrderId: 'wsPayOrderId',
  PaymentType: 'wsPayPaymentType',
  CreditCardNumber: 'wsPayCreditCardNumber',
  PaymentPlan: 'wsPayPaymentPlan',
  Success: 'wsPaySuccess',
  ApprovalCode: 'wsPayApprovalCode',
  Signature: 'wsPayConfirmationSignature',
};

const CheckoutSuccessPage = () => {
  const [params, setParams] = useState<UrlParams | null>(null);
  const purchaseCompletedRef = useRef(false);
  // const trackingCompletedRef = useRef(false);

  const { offerId } = useGeneralSoContext();
  const { offer, refetch: refetchOffer } = useGetOfferById();
  const [finishPaymentForOffer] = useFinishPayment();

  useEffect(() => {
    if (offerId && offer) {
      refetchOffer();
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const paramsObject: UrlParams = Array.from(queryParams.entries()).reduce(
      (acc, [key, value]) => {
        const newKey = keyMapping[key];
        if (newKey) acc[newKey] = value;
        return acc;
      },
      {} as UrlParams
    );
    setParams(paramsObject);
  }, []);

  useEffect(() => {
    if (!params || !offerId || purchaseCompletedRef.current) return;
    purchaseCompletedRef.current = true;

    const wsPayParams = {
      wsPayAmount: params.wsPayAmount,
      wsPayApprovalCode: params.wsPayApprovalCode,
      wsPayConfirmationSignature: params.wsPayConfirmationSignature,
      wsPayCreditCardNumber: params.wsPayCreditCardNumber,
      wsPayCustomerFirstName: params.wsPayCustomerFirstName,
      wsPayCustomerLastName: params.wsPayCustomerLastName,
      wsPayDateTime: params.wsPayDateTime,
      wsPayOrderId: params.wsPayOrderId,
      wsPayPartner: params.wsPayPartner,
      wsPayPaymentPlan: params.wsPayPaymentPlan,
      wsPayPaymentType: params.wsPayPaymentType,
      wsPaySTAN: params.wsPaySTAN,
      wsPayShoppingCartId: params.wsPayShoppingCartId,
      wsPaySuccess: params.wsPaySuccess,
    };

    finishPaymentForOffer(wsPayParams)
      .catch(() => {
        showMessage({
          type: 'error',
          message: i18n.t('common.messages.policyErrorMessage'),
          duration: 5000,
          fixed: true,
        });
      })
      .finally(() => {
        window.sessionStorage.clear();
      });
  }, [params, offerId, finishPaymentForOffer]);

  useEffect(() => {
    // if (!params || !offer || trackingCompletedRef.current) return;
    // trackingCompletedRef.current = true;
    // window.dataLayer = window.dataLayer || [];
    // window.dataLayer.push({ ecommerce: null });

    // const transactionId = params.wsPayOrderId || offer.offerNumber || offer._id;
    // const revenue = offer.productTypeSpecific?.payment?.totalPremium ?? 0;

    // window.dataLayer.push({
    //   event: 'eec.purchase',
    //   product_category: 'Osiguranje nekretnine',
    //   trackingData: {
    //     category: 'Osiguranje nekretnine',
    //     transactionId,
    //     revenue,
    //     currencyCode: 'EUR',
    //   },
    //   ecommerce: {
    //     transaction_id: transactionId,
    //     affiliation: '',
    //     value: revenue,
    //     tax: '',
    //     shipping: '',
    //     currency: 'EUR',
    //     coupon: '',
    //     items: [
    //       {
    //         item_id: offer._id ?? '',
    //         item_name: 'Osiguranje nekretnine',
    //         item_category: 'Osiguranje nekretnine',
    //         item_brand: offer.productTypeSpecific?.brandId?.name ?? '',
    //         price: revenue,
    //         quantity: 1,
    //       },
    //     ],
    //   },
    // }); TODO
  }, [params, offer]);

  const variant = params?.offline === 'true' ? 'offline' : 'online';

  return (
    <ContainerComponent>
      <Helmet>
        <title>Plaćanje uspješno | Osiguranje imovine - kompare.hr</title>
      </Helmet>
      <FlexStyle style={{ paddingTop: 32 }}>
        <ThankYouCard offer={offer} variant={variant} />
      </FlexStyle>
    </ContainerComponent>
  );
};

export default CheckoutSuccessPage;
